import { useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth, isDevBypass } from '@/auth/AuthProvider'
import { useDataset } from '@/data/DatasetProvider'

const Centered = ({ children }: { children: ReactNode }) => (
  <div
    className="page"
    style={{
      minHeight: '60vh',
      display: 'grid',
      placeContent: 'center',
      textAlign: 'center',
      gap: 14,
    }}
  >
    {children}
  </div>
)

/**
 * Microsoft's four-square mark, drawn rather than fetched.
 *
 * Their brand guidance fixes these four colours and the equal-square layout, so it
 * is inlined at the sizes they specify instead of loading an asset — one less
 * network round trip on the one screen a user sees before anything is cached, and
 * it cannot break if a CDN path changes.
 */
const MicrosoftMark = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" aria-hidden="true" focusable="false">
    <rect x="0" y="0" width="9.5" height="9.5" fill="#F25022" />
    <rect x="11.5" y="0" width="9.5" height="9.5" fill="#7FBA00" />
    <rect x="0" y="11.5" width="9.5" height="9.5" fill="#00A4EF" />
    <rect x="11.5" y="11.5" width="9.5" height="9.5" fill="#FFB900" />
  </svg>
)

/**
 * The sign-in button, to Microsoft's own spec: their mark at 20px, their label
 * wording, Segoe UI, and a surface that clears contrast in both themes.
 *
 * Deliberately NOT tinted with the app accent. A branded sign-in button is a
 * promise about where the credentials are going, and recolouring it to match a
 * palette makes it look like this app collects the password rather than handing
 * off to Microsoft.
 */
function MicrosoftSignIn({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        justifySelf: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        background: hover ? 'var(--surface-2)' : 'var(--surface)',
        color: 'var(--ink)',
        border: '1px solid var(--rule-2)',
        borderRadius: 8,
        padding: '11px 20px 11px 16px',
        fontFamily:
          '"Segoe UI", -apple-system, BlinkMacSystemFont, "IBM Plex Sans", sans-serif',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: 'var(--shadow)',
        transition: 'background 120ms ease',
      }}
    >
      <MicrosoftMark size={20} />
      Sign in with Microsoft
    </button>
  )
}

/** Sign-in gate. FCC tenant only — enforced by the single-tenant authority. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { account, ready, error, notice, signIn } = useAuth()

  if (isDevBypass) return <>{children}</>
  if (!ready) return <Centered><span className="eyebrow">Signing in…</span></Centered>

  if (error) {
    return (
      <Centered>
        <span className="eyebrow">Sign-in unavailable</span>
        <p className="body-note">{error}</p>
      </Centered>
    )
  }

  if (!account) {
    return (
      <div
        className="page"
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeContent: 'center',
          justifyItems: 'center',
          padding: '40px 24px',
        }}
      >
        {/*
          A card rather than text floating in the middle of a dark field. This is the
          only screen most people will judge the report by before they can see any of
          it, and a centred paragraph on a void reads as unfinished.
        */}
        <div
          className="card"
          style={{
            width: 'min(100%, 430px)',
            padding: '34px 34px 26px',
            textAlign: 'center',
            display: 'grid',
            justifyItems: 'center',
            gap: 0,
          }}
        >
          {/* The real FCC lockup, from the same asset the Power BI reports use. */}
          <div
            className="brand-mark"
            role="img"
            aria-label="Future Care Consultants"
            style={{ width: 96, height: 97 }}
          />

          <span
            className="eyebrow"
            style={{ marginTop: 22, letterSpacing: '0.1em', color: 'var(--ink-3)' }}
          >
            Revenue cycle management
          </span>
          <h1
            className="page-title"
            style={{ margin: '6px 0 0', fontSize: 'var(--t-page-title)' }}
          >
            Claims Efficiency Ledger
          </h1>
          <p
            className="body-note"
            style={{ margin: '10px 0 0', color: 'var(--ink-2)', textWrap: 'balance' }}
          >
            Cost per billed claim, throughput, quality and backlog by employee, job
            title, supervisor and department.
          </p>

          <div
            style={{
              width: '100%',
              height: 1,
              background: 'var(--rule)',
              margin: '26px 0 24px',
            }}
          />

          {/*
            Sign-in is normally automatic (AuthProvider). This says why the button is
            here instead: the automatic attempt came back empty, or failed.
          */}
          {notice && (
            <p
              role="alert"
              style={{
                margin: '0 0 16px',
                padding: '8px 12px',
                borderRadius: 7,
                background: 'var(--warn-bg)',
                color: 'var(--warn-ink)',
                fontSize: 13.5,
                lineHeight: 1.45,
              }}
            >
              {notice}
            </p>
          )}

          <MicrosoftSignIn onClick={() => void signIn()} />

          <p
            style={{
              margin: '22px 0 0',
              fontSize: 'var(--t-rank)',
              color: 'var(--ink-3)',
              lineHeight: 1.5,
            }}
          >
            Restricted to FCC accounts.
            <br />
            Contains named payroll — do not share access.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

/** Blocks a page until the bundle is in memory. */
export function RequireDataset({ children }: { children: ReactNode }) {
  const { dataset, loading, error, reload } = useDataset()

  if (loading) return <Centered><span className="eyebrow">Loading dataset…</span></Centered>

  if (error) {
    return (
      <Centered>
        <span className="eyebrow">Could not load the dataset</span>
        <p className="body-note">{error}</p>
        <button onClick={reload} style={{ justifySelf: 'center' }}>
          Retry
        </button>
      </Centered>
    )
  }

  if (!dataset) return <Centered><span className="eyebrow">No dataset</span></Centered>
  return <>{children}</>
}
